import { useEffect, useState } from 'react'
import { UserMenu } from '@/components/commons/user-menu'

import { LogoutIcon } from '@/components/ui/icons'
import { logout } from '@/features/auth/store/actions'
import { useAuthStore } from '@/features/auth/store/store'
import { GeometryCheckMap } from '@/features/geometry-check/components/geometry-check-map'
import { SubgeoList } from '@/features/geometry-check/components/subgeo-list'
import { ViewOnlyGeoSelect } from '@/features/geometry-check/components/view-only-geo-select'
import { useGetViewOnlyGeos } from '@/features/geometry-check/queries/use-get-view-only-geos'
import { useGetViewOnlySubgeos } from '@/features/geometry-check/queries/use-get-view-only-subgeos'
import { useGeometryCheckStore } from '@/features/geometry-check/store/geometry-check-store'
import type { ViewOnlySubGeo } from '@/features/geometry-check/types'
import { MapLoading } from '@/features/subgeo/components/map-loading'
import { SubGeoSearchParamsSchema } from '@/features/subgeo/types'
import {
	type ColorPair,
	DEFAULT_NEIGHBORHOOD_COLOR
} from '@/features/subgeo/utils/color-utils'
import { useParsedSearchParams } from '@/hooks/use-parsed-search-params'
import { useTranslation } from '@/translations/i18n'

const GeometryCheckMapContent = ({
	isLoading,
	error,
	getColor,
	shouldZoomToFit,
	viewOnlySubgeos
}: {
	isLoading: boolean
	error: Error | null
	getColor: (id: string) => ColorPair
	shouldZoomToFit: boolean
	viewOnlySubgeos: ViewOnlySubGeo[]
}) => {
	if (isLoading) {
		return <MapLoading />
	}
	if (error) {
		return <div>Error loading subgeos: {error.message}</div>
	}
	return (
		<GeometryCheckMap
			getColor={getColor}
			shouldZoomToFit={shouldZoomToFit}
			subgeos={viewOnlySubgeos}
		/>
	)
}

export const GeometryCheck = () => {
	const user = useAuthStore((state) => state.user)
	const { t } = useTranslation()
	const { searchParams, setSearchParams } = useParsedSearchParams(
		SubGeoSearchParamsSchema
	)
	const geo = searchParams.geo || ''

	const {
		setSelectedGeo,
		subgeoColorMap,
		initializeColors,
		highlightedSubgeoId,
		setHighlightedSubgeo
	} = useGeometryCheckStore()

	const [shouldZoomToFit, setShouldZoomToFit] = useState(false)

	const { remappedData: geos, isLoading: isLoadingGeos } = useGetViewOnlyGeos(
		geo ? { geoCode: geo } : undefined
	)
	const selectedGeoObject = geos.find((g) => g.geoCode === geo)
	const canFetchData = !!geo && !isLoadingGeos && !!selectedGeoObject

	const { viewOnlySubgeos, isLoading, error } = useGetViewOnlySubgeos(
		{ geoId: selectedGeoObject?.id || '' },
		{ enabled: canFetchData }
	)

	const handleGeoChange = (value: string): void => {
		setSelectedGeo(value)
		setSearchParams({ geo: value })
	}

	useEffect(() => {
		if (geo) {
			setSelectedGeo(geo)
		}
	}, [geo, setSelectedGeo])

	useEffect(() => {
		if (viewOnlySubgeos.length > 0) {
			initializeColors(viewOnlySubgeos)
		}
	}, [viewOnlySubgeos, initializeColors])

	useEffect(() => {
		if (geo && viewOnlySubgeos.length > 0) {
			setShouldZoomToFit(true)
			const timer = setTimeout(() => setShouldZoomToFit(false), 1000)
			return () => clearTimeout(timer)
		}
	}, [geo, viewOnlySubgeos.length])

	const getColor = (id: string) => {
		const baseColor = subgeoColorMap.get(id) || DEFAULT_NEIGHBORHOOD_COLOR

		if (highlightedSubgeoId) {
			if (id === highlightedSubgeoId) {
				return baseColor
			}
			return {
				...DEFAULT_NEIGHBORHOOD_COLOR,
				fillOpacity: 0.3
			}
		}

		return baseColor
	}

	return (
		<div className='relative m-0 h-dvh w-dvw overflow-hidden bg-neutral-white p-0'>
			{user && (
				<div className='fixed top-4 right-4 z-(--z-tooltip) flex rounded-[40px] border border-neutral-300 bg-neutral-white p-1 shadow-lg'>
					<UserMenu
						actions={[
							{
								label: t('common:logout'),
								icon: <LogoutIcon size={16} />,
								onClickCb: logout
							}
						]}
						size='sm'
						user={{
							name: user?.first_name ?? user.email,
							email: user.email,
							imageUrl: '',
							roleLabel: user.role
								? t(`auth:roles.${user.role}`)
								: undefined
						}}
					/>
				</div>
			)}

			<div className='absolute top-4 bottom-4 left-4 z-(--z-dropdown) flex w-[375px] flex-col gap-1.5'>
				<div className='flex w-full flex-col gap-1.5'>
					<ViewOnlyGeoSelect onChange={handleGeoChange} value={geo} />
				</div>
				{geo && (
					<SubgeoList
						getColor={getColor}
						highlightedSubgeoId={highlightedSubgeoId}
						isLoading={isLoading}
						onSubgeoClick={setHighlightedSubgeo}
						subgeos={viewOnlySubgeos}
					/>
				)}
			</div>

			<div className='absolute inset-0 z-(--z-map-base) overflow-hidden bg-neutral-100 [&_.geometry-check-tooltip]:border-none [&_.geometry-check-tooltip]:bg-transparent [&_.geometry-check-tooltip]:p-0 [&_.geometry-check-tooltip]:shadow-none [&_.leaflet-container]:h-full [&_.leaflet-container]:w-full [&_.leaflet-container]:bg-[#a0b8c5] [&_.leaflet-tile-pane]:absolute [&_.leaflet-tile-pane]:top-0 [&_.leaflet-tile-pane]:left-0'>
				<GeometryCheckMapContent
					error={error}
					getColor={getColor}
					isLoading={isLoading}
					shouldZoomToFit={shouldZoomToFit}
					viewOnlySubgeos={viewOnlySubgeos}
				/>
			</div>
		</div>
	)
}
